import { useEffect } from "react";
import { ArrowRight, MessageSquareText } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { SITE_CONFIG } from "@/config/siteConfig";

const COVERED = [
  { title: "We open your actual numbers.", sub: "Inquiry volume, response times, pipeline health, consult values." },
  { title: "We calculate the leak.", sub: "What missed calls and unworked inquiries cost you this month." },
  { title: "We map the gap.", sub: "Where the handoff breaks between an inquiry arriving and a consult getting booked." },
  { title: "You get a straight answer.", sub: "Whether we are the right fit for your practice. Plainly, before you spend another dollar." },
];

const SURVEY_URL = "https://api.leadconnectorhq.com/widget/survey/JrrS0j2SnMmmhSJL7Jmt";

export const Booking = () => {
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://link.msgsndr.com/js/form_embed.js";
    script.async = true;
    document.body.appendChild(script);
  }, []);

  return (
    <section id="book" data-testid="booking-section" className="py-24 md:py-36">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <SectionHeading
          eyebrow="The next step"
          title={
            <>
              Thirty minutes. Your numbers.{" "}
              <span className="italic text-champagne">An honest answer.</span>
            </>
          }
          copy="Fill out the short form and you will go straight to the founder's calendar to pick your time. If The Back-Office Engine is not right for your practice, you will hear it on the call."
        />

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <Reveal className="lg:col-span-5">
            <div className="border border-black/10 bg-coal p-8 md:p-10 h-full shadow-sm">
              <p className="font-mono text-xs tracking-[0.3em] uppercase text-champagne mb-8">On the call</p>
              <h3 className="font-display text-3xl font-light text-pearl">What we cover</h3>
              <p className="mt-3 text-sm text-zinc-500">
                30 minutes, video or phone. Your preference. Direct with the founder, not a sales rep.
              </p>
              <div className="mt-10 space-y-8">
                {COVERED.map((c, i) => (
                  <div key={i} className="flex gap-5" data-testid={`covered-item-${i}`}>
                    <span className="font-mono text-sm text-champagne mt-1">{String(i + 1).padStart(2, "0")}</span>
                    <div>
                      <p className="text-pearl font-normal">{c.title}</p>
                      <p className="mt-1 text-sm text-zinc-500 leading-relaxed">{c.sub}</p>
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-10 border-t border-black/10 pt-6 font-mono text-[11px] tracking-widest uppercase text-zinc-500">
                HIPAA-aware · No obligation · Direct with the founder
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-7">
            <div className="border border-champagne/40 bg-gradient-to-b from-champagne/10 to-white p-6 md:p-10 h-full shadow-[0_20px_60px_rgba(61,165,221,0.12)]">
              <h3 className="font-display text-3xl font-light text-pearl">
                Start your <span className="italic text-champagne">Revenue Leak Audit.</span>
              </h3>
              <p className="mt-3 text-sm text-zinc-500">
                Takes about 60 seconds. Right after, you pick your time on the calendar.
              </p>

              <div className="mt-8 bg-white border border-black/10 overflow-hidden">
                <iframe
                  src={SURVEY_URL}
                  style={{ border: "none", width: "100%", height: "1500px" }}
                  scrolling="no"
                  id="JrrS0j2SnMmmhSJL7Jmt"
                  title="survey"
                  data-cookie-consent="true"
                  data-cookie-consent-provider="auto"
                  data-testid="ghl-survey-iframe"
                />
              </div>

              <p className="mt-5 flex items-center justify-center gap-2 text-sm text-champagne" data-testid="speed-promise">
                <MessageSquareText size={15} />
                Expect a text from us in under 5 minutes.
              </p>
              <a
                href={SITE_CONFIG.calendarUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="calendar-continue-button"
                className="mt-3 flex items-center justify-center gap-2 text-sm text-zinc-500 hover:text-champagne transition-colors group"
              >
                Already finished the form? Pick your audit time
                <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
              </a>
              <p className="mt-6 text-[11px] text-zinc-400 leading-relaxed text-center">
                Your information is used only to prepare for your call and is never sold.
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
